/**
 * Direct Drive — richiesta #118.
 *
 * <activecampaign-form>: invia il modulo ad ActiveCampaign via JSONP, la
 * stessa strada che l'embed di AC usa per questo account, e a invio riuscito
 * apre il popup di conferma del tema invece di lasciare la pagina.
 *
 * La risposta di proc.php è uno script che chiama `_show_thank_you` o
 * `_show_error` sulla finestra: sono nomi imposti da AC, quindi globali. Qui
 * inoltrano all'istanza che sta inviando (una sola alla volta), e ai gestori
 * preesistenti quando nessuna nostra istanza è in attesa, così un embed di AC
 * incollato altrove nella pagina continua a funzionare.
 *
 * Validazione: quella nativa del browser (`required`, `type="email"`), che
 * scatta prima dell'evento submit.
 */
if (!customElements.get('activecampaign-form')) {
  class ActiveCampaignForm extends HTMLElement {
    static pending = null;

    constructor() {
      super();
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.submitButton = this.form.querySelector('[type="submit"]');
      this.errorBox = this.form.querySelector('[role="alert"]');

      // Fuori dal footer: `isolate` e i transform delle animazioni
      // intrappolerebbero `position: fixed`.
      this.modal = this.modal || this.querySelector('modal-dialog');
      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      // Nel theme editor la sezione si ri-renderizza: senza questo il dialog
      // spostato resterebbe orfano in <body> con un id duplicato.
      if (this.modal) this.modal.remove();
      if (ActiveCampaignForm.pending === this) ActiveCampaignForm.pending = null;
    }

    onSubmit(event) {
      event.preventDefault();
      if (ActiveCampaignForm.pending) return;

      this.hideError();
      this.setLoading(true);
      ActiveCampaignForm.pending = this;

      const params = new URLSearchParams(new FormData(this.form));
      params.set('jsonp', 'true');

      const script = document.createElement('script');
      script.src = `${this.dataset.endpoint}?${params.toString()}`;
      script.async = true;
      script.onerror = () => this.fail();
      // Se la risposta non ha chiamato nessuno dei due gestori (per esempio
      // un reindirizzamento configurato in AC), non si resta in caricamento.
      script.onload = () => {
        if (ActiveCampaignForm.pending === this) this.fail();
      };
      script.addEventListener('load', () => script.remove());
      script.addEventListener('error', () => script.remove());
      document.head.appendChild(script);
    }

    succeed(trackingUrl) {
      ActiveCampaignForm.pending = null;
      this.setLoading(false);
      this.form.reset();

      // Tracciamento del sito di AC, come nell'embed.
      if (trackingUrl) {
        const tracking = document.createElement('script');
        tracking.src = trackingUrl;
        tracking.async = true;
        document.head.appendChild(tracking);
      }

      if (this.modal && typeof this.modal.show === 'function') this.modal.show();
    }

    fail(message) {
      ActiveCampaignForm.pending = null;
      this.setLoading(false);
      if (!this.errorBox) return;

      // Il messaggio di AC può contenere HTML: se ne usa solo il testo.
      const holder = document.createElement('div');
      holder.innerHTML = message || '';
      const text = holder.textContent.trim();

      this.errorBox.textContent = text || this.errorBox.dataset.defaultMessage;
      this.errorBox.hidden = false;
      this.errorBox.focus();
    }

    hideError() {
      if (!this.errorBox) return;
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.disabled = isLoading;
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  const previousThankYou = window._show_thank_you;
  const previousError = window._show_error;

  window._show_thank_you = function (id, message, trackingUrl, email) {
    const pending = ActiveCampaignForm.pending;
    if (pending) return pending.succeed(trackingUrl);
    if (typeof previousThankYou === 'function') return previousThankYou.apply(this, arguments);
  };

  window._show_error = function (id, message, html) {
    const pending = ActiveCampaignForm.pending;
    if (pending) return pending.fail(message);
    if (typeof previousError === 'function') return previousError.apply(this, arguments);
  };

  customElements.define('activecampaign-form', ActiveCampaignForm);
}
